import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { firestore } from '../../firebase/config';
import { collection, addDoc, getDocs } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const BookingCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [trips, setTrips] = useState([]);
  const [formData, setFormData] = useState({
    tripId: '',
    passengerName: '',
    passengerEmail: '',
    passengerPhone: '',
    seats: 1,
    paymentMethod: 'Carte bancaire',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedTrip, setSelectedTrip] = useState(null);

  useEffect(() => {
    fetchTrips();
  }, []);

  useEffect(() => {
    if (formData.tripId) {
      const trip = trips.find(t => t.id === formData.tripId);
      setSelectedTrip(trip);
    }
  }, [formData.tripId, trips]);

  const fetchTrips = async () => {
    setLoadingData(true);
    try {
      const tripsCollection = collection(firestore, 'trips');
      const tripsSnapshot = await getDocs(tripsCollection);
      
      if (tripsSnapshot.empty) {
        // Données de démonstration
        const demoTrips = [
          { 
            id: '1', 
            name: 'Paris - Lyon',
            departureDate: '2025-04-15T08:00:00',
            arrivalDate: '2025-04-15T12:30:00',
            price: 45.99,
            availableSeats: 25,
            status: 'Scheduled'
          },
          { 
            id: '2', 
            name: 'Lyon - Marseille',
            departureDate: '2025-04-16T10:00:00',
            arrivalDate: '2025-04-16T13:00:00',
            price: 35.50,
            availableSeats: 30,
            status: 'Scheduled'
          }
        ];
        setTrips(demoTrips);
      } else {
        const fetchedTrips = tripsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(trip => trip.status === 'Scheduled' && trip.availableSeats > 0);
        
        setTrips(fetchedTrips);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des voyages',
        severity: 'error'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tripId) {
      newErrors.tripId = 'Veuillez sélectionner un voyage';
    }

    if (!formData.passengerName.trim()) {
      newErrors.passengerName = 'Le nom du passager est requis';
    }

    if (!formData.passengerEmail.trim()) {
      newErrors.passengerEmail = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.passengerEmail)) {
      newErrors.passengerEmail = 'Email invalide';
    }

    if (!formData.passengerPhone.trim()) {
      newErrors.passengerPhone = 'Le téléphone est requis';
    }

    if (!formData.seats || formData.seats < 1) {
      newErrors.seats = 'Le nombre de places doit être au moins 1';
    }

    if (selectedTrip && formData.seats > selectedTrip.availableSeats) {
      newErrors.seats = `Seulement ${selectedTrip.availableSeats} places disponibles`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const totalPrice = selectedTrip ? selectedTrip.price * formData.seats : 0;
      
      const bookingData = {
        ...formData,
        tripName: selectedTrip?.name || '',
        departureDate: selectedTrip?.departureDate || '',
        arrivalDate: selectedTrip?.arrivalDate || '',
        totalPrice,
        status: 'Confirmed',
        paymentStatus: 'Paid',
        bookingDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(firestore, 'bookings'), bookingData);

      setSnackbar({
        open: true,
        message: 'Réservation créée avec succès',
        severity: 'success'
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/admin/bookings');
      }, 1500);
    } catch (error) {
      console.error('Error creating booking:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la création de la réservation',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/bookings');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const calculateTotalPrice = () => {
    if (selectedTrip && formData.seats) {
      return (selectedTrip.price * formData.seats).toFixed(2);
    }
    return '0.00';
  };

  if (loadingData) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            style={{ marginRight: '16px' }}
          >
            Retour
          </Button>
          <Typography variant="h4" component="h1">
            Nouvelle réservation
          </Typography>
        </Box>

        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations du voyage
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl 
                  className={classes.formControl} 
                  error={Boolean(errors.tripId)}
                  variant="outlined"
                >
                  <InputLabel>Voyage</InputLabel>
                  <Select
                    name="tripId"
                    value={formData.tripId}
                    onChange={handleChange}
                    label="Voyage"
                  >
                    <MenuItem value="">
                      <em>Sélectionner un voyage</em>
                    </MenuItem>
                    {trips.map((trip) => (
                      <MenuItem key={trip.id} value={trip.id}>
                        {trip.name} - {new Date(trip.departureDate).toLocaleDateString('fr-FR')} - {trip.price}€ ({trip.availableSeats} places)
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tripId && <FormHelperText>{errors.tripId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </div>

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations du passager
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Nom complet"
                  name="passengerName"
                  value={formData.passengerName}
                  onChange={handleChange}
                  error={Boolean(errors.passengerName)}
                  helperText={errors.passengerName}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Email"
                  name="passengerEmail"
                  type="email"
                  value={formData.passengerEmail}
                  onChange={handleChange}
                  error={Boolean(errors.passengerEmail)}
                  helperText={errors.passengerEmail}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Téléphone"
                  name="passengerPhone"
                  value={formData.passengerPhone}
                  onChange={handleChange}
                  error={Boolean(errors.passengerPhone)}
                  helperText={errors.passengerPhone}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Nombre de places"
                  name="seats"
                  type="number"
                  value={formData.seats}
                  onChange={handleChange}
                  error={Boolean(errors.seats)}
                  helperText={errors.seats}
                  variant="outlined"
                  InputProps={{
                    inputProps: { min: 1, max: selectedTrip?.availableSeats || 50 }
                  }}
                  required
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations de paiement
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} variant="outlined">
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    label="Méthode de paiement"
                  >
                    <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
                    <MenuItem value="PayPal">PayPal</MenuItem>
                    <MenuItem value="Virement bancaire">Virement bancaire</MenuItem>
                    <MenuItem value="Espèces">Espèces</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Prix total"
                  value={calculateTotalPrice()}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.formControl}
                  label="Notes (optionnel)"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.buttons}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Créer la réservation'}
            </Button>
          </div>
        </form>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingCreatePage;
